import { useCallback, useMemo, useState, type ReactNode } from "react";
import { readPrincipal } from "./jwt";
import { clearToken, getAccessToken, saveToken } from "./session";
import { login as passwordGrant } from "./login";
import { canSee, canSeeAny } from "./authorities";
import { AuthContext, type AuthState } from "./context";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [principal, setPrincipal] = useState(() => readPrincipal(getAccessToken()));

  const signIn = useCallback(async (username: string, password: string, rememberMe = false) => {
    const token = await passwordGrant(username, password);
    saveToken(token, rememberMe);
    const next = readPrincipal(token.access_token);
    if (!next) throw new Error("The sign-in response did not contain a usable token.");
    setPrincipal(next);
    return next;
  }, []);

  const signOut = useCallback(() => {
    clearToken();
    setPrincipal(null);
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      principal,
      isAuthenticated: principal !== null,
      signIn,
      signOut,
      can: (authority) => canSee(principal, authority),
      canAny: (authorities) => canSeeAny(principal, authorities),
    }),
    [principal, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
